import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import GiftCard from '../components/GiftCard';
import { supabase } from '../lib/supabase';

interface Gift {
  id: string;
  name: string;
  description: string;
  image_url: string;
  price: number;
  pix_key: string;
  gift_purchases?: {
    purchaser_name: string;
    purchased_at: string;
  }[];
}

const GiftsPage: React.FC = () => {
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGifts = async () => {
      try {
        const { data, error } = await supabase
          .from('gifts')
          .select('*, gift_purchases(purchaser_name, purchased_at)')
          .order('name');
          
        if (error) throw error;
        
        if (data) {
          setGifts(data);
        }
      } catch (error) {
        console.error('Error fetching gifts:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchGifts();
  }, []);

  const handlePurchase = async (
    id: string, 
    purchaserName: string, 
    phone: string, 
    message: string
  ) => {
    try {
      const gift = gifts.find(g => g.id === id);
      if (!gift) {
        throw new Error('Gift not found');
      }

      const { error } = await supabase
        .from('gift_purchases')
        .insert({
          gift_id: id,
          purchaser_name: purchaserName,
          phone: phone,
          message: message
        });
        
      if (error) throw error;
      
      // Update local state
      const newPurchase = {
        purchaser_name: purchaserName,
        purchased_at: new Date().toISOString()
      };
      
      setGifts(gifts.map(gift => 
        gift.id === id 
          ? { 
              ...gift, 
              gift_purchases: [...(gift.gift_purchases || []), newPurchase]
            }
          : gift
      ));
      
      window.location.href = '/obrigado';
    } catch (error) {
      console.error('Error purchasing gift:', error);
      throw error;
    }
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <PageHeader 
        title="Lista de Presentes" 
        subtitle="Escolha um presente simbólico e contribua com o início da nossa vida a dois.
Obrigado por fazer parte desse momento tão especial!"
      />
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wedding-blue-600"></div>
        </div>
      ) : (
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {gifts.map(gift => (
              <GiftCard
                key={gift.id}
                id={gift.id}
                name={gift.name}
                description={gift.description}
                imageUrl={gift.image_url}
                price={gift.price}
                pixKey={gift.pix_key}
                isPurchased={false}
                purchaseCount={(gift.gift_purchases || []).length}
                onPurchase={handlePurchase}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftsPage;